import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { scroller } from "react-scroll";
import Sidebar from "../Nav/Sidebar";
import Backdrop from "../Elements/Backdrop";
import LogoIcon from "../../assets/svg/Logo";
import BurgerIcon from "../../assets/svg/BurgerIcon";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";

export default function TopNavbar() {
  const [y, setY] = useState(window.scrollY);
  const [sidebarOpen, toggleSidebar] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const isHomePath = (path) => {
    return path === "/" || /^\/service-desc(\/|$)/.test(path);
  };

  const [mode, setMode] = useState(() => (isHomePath(window.location.pathname) ? "home" : "admin"));
  const observerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMode(location.pathname === "/" ? "home" : "admin");
  }, [location.pathname]);

  const handleNavTo = (section) => {
    if (mode === "admin") {
      if (section === "my-services") {
        navigate("/services");
      } else if (section === "client-messages") {
        navigate("/messages");
      } else {
        navigate("/services");
      }
      return;
    }

    setActiveSection(section);
    if (location.pathname === "/") {
      scroller.scrollTo(section, {
        duration: 600,
        smooth: true,
        offset: -80,
      });
    } else {
      navigate("/", { state: { scrollTo: section } });
    }
  };

  useEffect(() => {
    if (location.pathname !== "/") {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const selectors = ["#home", "#projects", "#contact"];
    const elements = selectors.map((s) => document.querySelector(s)).filter(Boolean);
    if (elements.length === 0) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          setActiveSection(id);
        }
      },
      {
        root: null,
        threshold: [0.55, 0.6, 0.7],
      }
    );

    elements.forEach((el) => obs.observe(el));
    observerRef.current = obs;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setActiveSection(target);
      if (location.pathname === "/") {
        scroller.scrollTo(target, {
          duration: 600,
          smooth: true,
          offset: -80,
        });
      }
    }
  }, [location.state, location.pathname]);

  const goToAdmin = () => {
    navigate("/services");
    setMode("admin");
  };

  const goHomeMode = () => {
    navigate("/");
    setMode("home");
  };



  useEffect(() => {
    setMode(isHomePath(location.pathname) ? "home" : "admin");
  }, [location.pathname]);

  return (
    <>
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <Backdrop toggleSidebar={toggleSidebar} />}
      <Wrapper className="flexCenter animate whiteBg" style={y > 100 ? { height: "60px" } : { height: "80px" }}>
        <NavInner className="container flexSpaceCenter">
          <LogoBtn onClick={() => { goHomeMode(); handleNavTo("home"); }} aria-label="Go to home">
            <div style={{ textAlign: "center" }}>
              <img
                src="/nava-logo.png"
                alt="Nava Energy logo"
                style={{
                  width: 80,
                  height: 50,
                  objectFit: "contain",
                  display: "block",
                  margin: "0 auto",
                }}
                loading="lazy"
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontSize: "20px", fontWeight: "800", margin: 0, color: "#13365c" }}>NAVA</h1>
              <h2 style={{ fontSize: "14px", fontWeight: "800", margin: 0, color: "#179fd6" }}>ENERGY</h2>
            </div>
          </LogoBtn>

          <BurderWrapper className="pointer" onClick={() => toggleSidebar(!sidebarOpen)}>
            <BurgerIcon />
          </BurderWrapper>

          <UlWrapper className="flexNullCenter">
            {mode === "home" ? (
              <>
                <NavItem>
                  <NavButton onClick={() => handleNavTo("home")} active={activeSection === "home"}>
                    Home
                  </NavButton>
                </NavItem>

                <NavItem>
                  <NavButton onClick={() => handleNavTo("projects")} active={activeSection === "projects"}>
                    Services
                  </NavButton>
                </NavItem>

                <NavItem>
                  <NavButton onClick={() => handleNavTo("contact")} active={activeSection === "contact"}>
                    Contact
                  </NavButton>
                </NavItem>
              </>
            ) : (
              <>
                <NavItem>
                  <NavButton onClick={() => handleNavTo("my-services")} active={location.pathname === "/services"}>
                    My Services
                  </NavButton>
                </NavItem>

                <NavItem>
                  <NavButton onClick={() => handleNavTo("client-messages")} active={location.pathname === "/messages"}>
                    Client Messages
                  </NavButton>
                </NavItem>
              </>
            )}
          </UlWrapper>

          <UlWrapperRight className="flexNullCenter">
            {mode === "home" ? (
              <>
                <li className="semiBold font15 pointer">
                  <a href="/" style={{ padding: "10px 30px 10px 0" }}>
                    Log in
                  </a>
                </li>
                <li className="semiBold font15 pointer flexCenter">
                  <button
                    onClick={goToAdmin}
                    className="radius8 lightBg"
                    style={{ padding: "10px 15px", border: 0, background: "transparent", cursor: "pointer" }}
                  >
                    Admin
                  </button>
                </li>
              </>
            ) : (
              <>
                <li className="semiBold font15 pointer">
                  <button
                    onClick={goHomeMode}
                    style={{ padding: "10px 30px 10px 0", border: 0, background: "transparent", cursor: "pointer" }}
                  >
                    Home
                  </button>
                </li>
                <li className="semiBold font15 pointer flexCenter">
                  <a href="/" className="radius8 lightBg" style={{ padding: "10px 15px" }}>
                    Log in
                  </a>
                </li>
              </>
            )}
          </UlWrapperRight>
        </NavInner>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.nav`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
`;
const NavInner = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BurderWrapper = styled.button`
  position: absolute;
  left: 12px;              
  top: 50%;
  transform: translateY(-50%);
  z-index: 10001;        
  outline: none;
  border: 0px;
  background-color: transparent;
  height: 40px;
  padding: 8px;
  display: none;

  @media (max-width: 760px) {
    display: block;
  }
`;

const LogoBtn = styled.button`
  background: transparent;
  border: 0;
  display: flex;
  align-items: center;
  cursor: pointer;

  @media (max-width: 760px) {
    margin-left: 48px;
  }
`;

const UlWrapper = styled.ul`
  display: flex;
  gap: 8px;
  list-style: none;
  align-items: center;
  margin: 0;
  padding: 0;
  @media (max-width: 760px) {
    display: none;
  }
`;

const UlWrapperRight = styled.ul`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  padding: 0;
  @media (max-width: 760px) {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style: none;
`;

const NavButton = styled.button`
  padding: 10px 15px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-weight: 700;
  color: inherit;
  position: relative;
  outline: none;

  &::after {
    content: "";
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: -8px;
    height: 3px;
    background: ${(p) => (p.active ? "#7620ff" : "transparent")};
    border-radius: 3px;
    transition: background 160ms ease;
  }

  &:hover::after {
    background: ${(p) => (p.active ? "#7620ff" : "rgba(118,32,255,0.12)")};
  }
`;

const ActionButton = styled.button`
  padding: 10px 15px;
  border-radius: 8px;
  border: 0;
  background: #f5f7fb;   
  color: #13365c;          
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;  
  transition: transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  }

  &:active {
    transform: translateY(0);
  }
`;
